// Header.jsx — iMatrix Technologies
import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaTimes, FaChevronRight, FaChevronDown, FaChartLine, FaCogs, FaCode, FaMobileAlt, FaLaptop, FaVideo, FaFingerprint, FaPrint, FaServer, FaRobot, FaPhone, FaEnvelope, FaInstagram, FaLinkedin, FaYoutube, FaStar, FaWhatsapp } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Navigation.css';

const NAV_LINKS = [
  { label: 'Home',       to: '/' },
  { label: 'Milestones', to: '/timelines' },
  { label: 'Customers',  to: '/Client' },
  { label: 'Careers',    to: '/career_pages' },
  { label: 'About',      to: '/AboutPage' },
  { label: 'Contact',    to: '/contact' },
];

const CATEGORIES = [
  { name: 'Tally Solutions', route: '/tallyprime', icon: <FaChartLine /> },
  { name: 'IT Services', route: '/ServicePage', icon: <FaCogs /> },
  { name: 'Web Development', route: '/WebDevelopment', icon: <FaCode /> },
  { name: 'Mobile Apps', route: '/MobileDeveloper', icon: <FaMobileAlt /> },
  { name: 'Hardware', route: '/HardwareServices', icon: <FaLaptop /> },
  { name: 'CCTV Security', route: '/SecuritySystem', icon: <FaVideo /> },
  { name: 'Biometrics', route: '/BIO', icon: <FaFingerprint /> },
  { name: 'Printer Services', route: '/PrinterSoftware', icon: <FaPrint /> },
];

const Header = ({ splashState = 'completed', logoRef, hideLogo }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [partnerClicks, setPartnerClicks] = useState(0);
  const [showRibbon, setShowRibbon] = useState(false);
  const [isPulled, setIsPulled] = useState(false);
  const [doorOpen, setDoorOpen] = useState(false);

  // ── Easter Egg: Right-click custom context menu on logo ──
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y }

  const handleLogoContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  // Close context menu on any outside click or Escape
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e) => {
      if (e.key === 'Escape') {
        setCtxMenu(null);
        return;
      }
      const menuEl = document.querySelector('.custom-ctx-menu');
      if (menuEl && !menuEl.contains(e.target)) {
        setCtxMenu(null);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [ctxMenu]);

  const handlePartnerLogoClick = () => {
    setPartnerClicks(prev => {
      const next = prev + 1;
      if (next >= 7) {
        setShowRibbon(true);
        return 0;
      }
      return next;
    });
  };

  const handleRibbonPull = () => {
    if (isPulled) return;
    setIsPulled(true);
    
    // Step 1: Lever flips, LED goes red
    setTimeout(() => {
      setDoorOpen(true); // Step 2: Door panels close from left and right
    }, 300);

    // Step 3: Route while doors are fully shut (around 1.3s after pull)
    setTimeout(() => {
      navigate('/admin/login');
    }, 1300);

    // Step 4: After doors open completely and finish animation, reset state
    setTimeout(() => {
      setShowRibbon(false);
      setIsPulled(false);
      setDoorOpen(false);
    }, 3000);
  };

  const [isLargeScreen, setIsLargeScreen] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1200 : true);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Scrolled state for background change
      setScrolled(currentScrollY > 20);

      // Hide/Show logic during active scrolling
      if (currentScrollY > lastScrollY && currentScrollY > 80) {
        // Scrolling down - hide immediately
        if (!showMenu && !isHovered && !isDropdownOpen) {
          setVisible(false);
        }
      } else {
        // Scrolling up or at the very top - show immediately
        setVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, showMenu, isHovered, isDropdownOpen]);

  // Ensure navbar remains visible when interactive overlays are active
  useEffect(() => {
    if (showMenu || isDropdownOpen || isHovered) {
      setVisible(true);
    }
  }, [showMenu, isDropdownOpen, isHovered]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideSidebar = sidebarRef.current && !sidebarRef.current.contains(e.target);
      const isOutsideButton = menuButtonRef.current && !menuButtonRef.current.contains(e.target);
      
      if (isOutsideSidebar && isOutsideButton && showMenu) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    setShowMenu(false);
    setIsDropdownOpen(false); // Close dropdown on page changes
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Clean up any pending dropdown close timers on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 300); // Responsive 300ms delay instead of sluggish 1000ms
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    document.body.style.overflow = showMenu ? 'hidden' : '';
  }, [showMenu]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* 
      <div className="wna-global-marquee">
        <div className="wna-marquee-track">
          <span>🔥 What's New: Check out our latest updates, product releases, and exciting new announcements! <Link to="/whats-new">View Updates &rarr;</Link></span>
          <span>🔥 What's New: Check out our latest updates, product releases, and exciting new announcements! <Link to="/whats-new">View Updates &rarr;</Link></span>
        </div>
      </div>
      */}
      {showRibbon && (
        <div 
          className={`secret-lever-wrap ${isPulled ? 'lever-flipped' : ''}`}
          onClick={handleRibbonPull}
          aria-hidden="true"
        >
          {/* Skeuomorphic wall-mounted metal switchplate */}
          <div className="lever-mount">
            <div className="mount-bolt mount-bolt--top" />
            
            {/* The slot track the switch pivots within */}
            <div className="lever-track">
              <div className="lever-arm-pivot" />
              <div className="lever-arm">
                <div className="lever-knob" />
              </div>
            </div>

            <div className="mount-bolt mount-bolt--bot" />
            <div className={`lever-indicator ${isPulled ? 'active' : ''}`} />
          </div>
        </div>
      )}
      {/* Secret Door — slides in from edges, locks shut, then cracks open */}
      {doorOpen && (
        <div className="secret-door-overlay">
          {/* Left panel sweeps in from left edge */}
          <div className="secret-door-half secret-door-half--left" />
          {/* Right panel sweeps in from right edge */}
          <div className="secret-door-half secret-door-half--right" />
        </div>
      )}
      <nav 
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${!visible ? 'navbar--hidden' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="logo-group">
          {/* Custom right-click context menu easter egg */}
          {ctxMenu && (
            <div
              className="custom-ctx-menu"
              style={{ top: Math.min(ctxMenu.y, window.innerHeight - 280), left: Math.min(ctxMenu.x, window.innerWidth - 260) }}
              onClick={() => setCtxMenu(null)}
            >
              <div className="ctx-menu-header">iMatrix Technologies</div>
              <a href="tel:+919841609990" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaPhone /></span>
                +91 98416 09990
              </a>
              <a href="mailto:admin@imatrixtechnologies.com" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaEnvelope /></span>
                admin@imatrixtechnologies.com
              </a>
              <a href="https://wa.me/919841609990" target="_blank" rel="noopener noreferrer" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaWhatsapp /></span>
                WhatsApp Quick Chat
              </a>
              <div className="ctx-menu-divider" />
              <span className="ctx-menu-item" style={{ cursor: 'default', color: '#f59e0b', fontWeight: 700 }}>
                <span className="ctx-menu-item__icon"><FaStar /></span>
                Tally 5★ Authorized Partner
              </span>
              <div className="ctx-menu-divider" />
              <a href="https://www.instagram.com/imatrix.t/" target="_blank" rel="noopener noreferrer" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaInstagram /></span>
                Instagram
              </a>
              <a href="https://in.linkedin.com/company/imatrix-t" target="_blank" rel="noopener noreferrer" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaLinkedin /></span>
                LinkedIn
              </a>
              <a href="https://www.youtube.com/@imatrixtechnologies1833" target="_blank" rel="noopener noreferrer" className="ctx-menu-item">
                <span className="ctx-menu-item__icon"><FaYoutube /></span>
                YouTube
              </a>
            </div>
          )}

          <div className="logo">
            <Link
              to="/"
              aria-label="iMatrix Technologies Home"
              onContextMenu={handleLogoContextMenu}
            >
              <img src="/images/logo.png" alt="iMatrix" />
            </Link>
          </div>


          {isLargeScreen && (splashState === 'attaching' || splashState === 'completed') ? (
            <div className="header-partner-badge">
              <img
                ref={logoRef}
                src="/gvla-5star/gvla-5star.png"
                alt="Tally 5 Star Partner"
                className="header-star-logo"
                style={{ visibility: hideLogo ? 'hidden' : 'visible', cursor: 'pointer' }}
                onClick={handlePartnerLogoClick}
              />
              <motion.div
                className="header-star-info"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.5, ease: 'easeOut' }}
              >
                <span className="header-star-badge">Authorized 5-Star Partner</span>
                <span className="header-star-sub">Tally Solutions Pvt. Ltd.</span>
              </motion.div>
            </div>
          ) : (
            isLargeScreen && <div className="header-partner-badge-spacer" style={{ width: '230px', borderLeft: '1px solid transparent' }} />
          )}
        </div>

        <ul className="nav-links">
          {NAV_LINKS.slice(0, 1).map((link) => (
            <li key={link.to}>
              <Link to={link.to} className={`nav-link ${isActive(link.to) ? 'active' : ''}`}>
                {link.label}
              </Link>
            </li>
          ))}

          {/* ── Services Mega Dropdown ── */}
          <li 
            className={`nav-item dropdown mega-dropdown ${isDropdownOpen ? 'active' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="nav-link dropdown-toggle" data-tour="services-nav" onClick={handleDropdownClick}>
              Our Services
              <FaChevronDown className="chevron-icon" />
            </button>
            <div className="dropdown-menu mega-menu">
              <div className="mega-menu-grid">
                {/* Column 1: Software & Apps */}
                <div className="mega-menu-col">
                  <h4 className="mega-menu-title">Software Solutions</h4>
                  <Link to="/tallyprime" data-tour="tally-nav" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaChartLine className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">TallyPrime</span>
                      <span className="dropdown-desc">Business Management</span>
                    </div>
                  </Link>
                  <Link to="/TallyIra" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaRobot className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">TallyPrime Ira</span>
                      <span className="dropdown-desc">AI Automation & Reports</span>
                    </div>
                  </Link>
                  <Link to="/WebDevelopment" data-tour="solutions-nav" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaCode className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">Web Development</span>
                      <span className="dropdown-desc">Custom Sites & Portals</span>
                    </div>
                  </Link>
                  <Link to="/MobileDeveloper" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaMobileAlt className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">App Development</span>
                      <span className="dropdown-desc">iOS & Android Apps</span>
                    </div>
                  </Link>
                </div>

                {/* Column 2: Infrastructure & Security */}
                <div className="mega-menu-col">
                  <h4 className="mega-menu-title">Infrastructure & Security</h4>
                  <Link to="/ServerPage" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaServer className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">Server Solutions</span>
                      <span className="dropdown-desc">Hosting & Data Integrity</span>
                    </div>
                  </Link>
                  <Link to="/SecuritySystem" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaVideo className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">CCTV Systems</span>
                      <span className="dropdown-desc">Smart Surveillance</span>
                    </div>
                  </Link>
                  <Link to="/BIO" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaFingerprint className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">Biometrics</span>
                      <span className="dropdown-desc">Attendance & Access</span>
                    </div>
                  </Link>
                </div>

                {/* Column 3: Specialized Services */}
                <div className="mega-menu-col">
                  <h4 className="mega-menu-title">Specialized Services</h4>
                  <Link to="/HardwareServices" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaLaptop className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">Hardware Sales</span>
                      <span className="dropdown-desc">Laptops & Peripherals</span>
                    </div>
                  </Link>
                  <Link to="/PrinterSoftware" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                    <FaPrint className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">Printer Solutions</span>
                      <span className="dropdown-desc">Service & Software</span>
                    </div>
                  </Link>
                  <Link to="/ServicePage" className="dropdown-item all-services-link" onClick={() => setIsDropdownOpen(false)}>
                    <FaCogs className="dropdown-icon" />
                    <div className="dropdown-content">
                      <span className="dropdown-title">All Services</span>
                      <span className="dropdown-desc">Explore full suite</span>
                    </div>
                  </Link>
                </div>

                {/* Column 4: Premium Showcase Card */}
                <div className="mega-menu-col mega-menu-showcase">
                  <div className="showcase-card">
                    <div>
                      <span className="showcase-tag">TALLY 5-STAR PARTNER</span>
                      <h4 className="showcase-title">Enterprise Tally Customization</h4>
                      <p className="showcase-desc">
                        Optimize financial workflows, deploy custom modules (PR, GST), and host with lightning-fast, high-integrity server performance.
                      </p>
                    </div>
                    <Link to="/contact" className="showcase-cta" onClick={() => setIsDropdownOpen(false)}>
                      Consult an Expert
                      <FaChevronRight className="cta-arrow" />
                    </Link>
                  </div>
                </div>

              </div>
            </div>
          </li>

          {NAV_LINKS.slice(1).map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className={`nav-link ${isActive(link.to) ? 'active' : ''}`}
                data-tour={link.to === '/contact' ? 'contact-nav' : undefined}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        <button 
          ref={menuButtonRef}
          className="menu-icon" 
          onClick={() => setShowMenu(!showMenu)}
          aria-label={showMenu ? "Close menu" : "Open menu"}
        >
          <div className={`sandwich-bars ${showMenu ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </nav>

      <div className={`nav-overlay ${showMenu ? 'visible' : ''}`} onClick={() => setShowMenu(false)} />

      <aside ref={sidebarRef} className={`sidebar ${showMenu ? 'open' : ''}`}>
        <div className="sidebar-header" style={{ marginBottom: '12px' }}>
          <button className="sidebar-close-btn" onClick={() => setShowMenu(false)}>
            <FaTimes />
          </button>
        </div>

        <ul className="sidebar-nav">
          {NAV_LINKS.map((link) => (
            <li key={link.to} className={isActive(link.to) ? 'active' : ''} onClick={() => navigate(link.to)}>
              {link.label}
            </li>
          ))}
        </ul>

        <div className="sub-item-container">
          <div className="sub-item-label">Our Services</div>
          <div className="card-list">
            {CATEGORIES.map((cat) => (
              <div key={cat.route} className="card" onClick={() => navigate(cat.route)}>
                <div className="card-icon">{cat.icon}</div>
                <p>{cat.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="sidebar-footer">
          <button className="btn-primary" style={{ width: '100%', padding: '12px' }} onClick={() => navigate('/contact')}>
            Contact Us
          </button>
        </div>
      </aside>
    </>
  );
};

export default Header;

